<template>
  <FlexLayout>
    <vue-loading :active="isLoading" :is-full-page="true" />

    <BlockContainer id="container-customer-card-digital" :title="$t('customerCard.subTitle.digital')" v-if="config.SHOW_CUSTOMER_QR_CODE" role="region">
      <div class="qr-section">
        <radial-progress-bar
            :diameter="config.QRCODE_RADIAL_PROGRESSBAR_SIZE"
            :completed-steps="30 - qrCodeSeconds"
            :total-steps="30"
            startColor='var(--primary-color)'
            stopColor='var(--primary-color)'
            innerStrokeColor="lightgray"
            role="progressbar"
            :aria-valuemin="0"
            :aria-valuemax="30"
            :aria-valuenow="qrCodeSeconds"
            :aria-valuetext="qrCodeSeconds"
            :aria-label="$t('customerCard.ariaLabel.timerQR',[qrCodeSeconds])">
          <qr-code
              :value="qrCode"
              :size="config.QRCODE_SIZE"
              :aria-label="$t('customerCard.ariaLabel.digitalQR')"
              role="img">
          </qr-code>
          {{ qrCodeSeconds }}s
        </radial-progress-bar>
      </div>
    </BlockContainer>
    <BlockContainer id="container-customer-card-info" :title="$t('customerCard.subTitle.info')" role="region">

      <p v-if="config.ACCOUNT.showWondCustomerDataOnly" role="heading" aria-level="3"><span>{{ $t('customerCard.label.holder') }}:</span> <span>{{ wondCustomer.firstName }} {{ wondCustomer.description }}</span></p>
      <p v-else role="heading" aria-level="3"><span>{{ $t('customerCard.label.holder') }}:</span> <span>{{ customer.firstName }} {{ customer.lastName }}</span></p>
      <p role="heading" aria-level="3"><span>{{ $t('customerCard.label.number') }}:</span> <span>{{ wondCustomer.cardNum }}</span></p>
      <p v-if="additionalCards.length" role="heading" aria-level="3">
        <span>{{ $t('customerCard.label.additionalCardNumbers') }}:</span>
        <span class="additional-card-numbers">
          <span v-for="additionalCard in additionalCards" :key="additionalCard.cardNum">{{ additionalCard.cardNum }}</span>
        </span>
      </p>
      <!-- Show balances according to cardType -->

      <template v-if="wondCustomer.cardType">
        <p v-if="wondCustomer.cardType === CardType.PREPAYMENT" role="heading" aria-level="3">
          <span>{{ $t(balanceLabelKey) }}:</span> <span>{{ wondCustomer.formattedPrepayBalanceCash }}</span>
        </p>
        <p v-else-if="wondCustomer.cardType === CardType.CREDIT" role="heading" aria-level="3">
          <span>{{ $t('customerCard.label.balanceCredit') }}:</span> <span>{{ wondCustomer.formattedCreditBalance }}</span>
        </p>
        <template v-else-if="wondCustomer.cardType === CardType.PREPAYMENT_AND_CREDIT || wondCustomer.cardType === CardType.CREDIT_AND_PREPAYMENT">
          <p role="heading" aria-level="3">
            <span>{{ $t(balanceLabelKey) }}:</span> <span>{{ wondCustomer.formattedPrepayBalanceCash }}</span>
          </p>
          <p role="heading" aria-level="3">
            <span>{{ $t('customerCard.label.balanceCredit') }}:</span> <span>{{ wondCustomer.formattedCreditBalance }}</span>
          </p>
        </template>
      </template>

      <template v-if="config.CUSTOMER_CARD.showPendingTopUp && pendingCustomerCardPayments.length > 0">
        <p v-for="pendingTopup in pendingCustomerCardPayments" :key="pendingTopup.paymentTimestamp" role="heading" aria-level="3">
          <strong>{{ $t('customerCard.label.pending') }}:</strong>
          {{formatPrice(pendingTopup.amount)}} ({{formatDate(pendingTopup.paymentTimestamp)}})
        </p>
      </template>

      <template #actions>
        <Button v-if="config.CUSTOMER_CARD.topUp.enabled && wondCustomer.cardType !== CardType.IDENTIFICATION && wondCustomer.cardType !== CardType.CREDIT"  @click="goToTopUp()" :aria-label="$t('customerCard.ariaLabel.charge')" aria-live="polite">{{ $t('customerCard.button.charge') }}</Button>
        <Button v-if="config.CUSTOMER_CARD.autoTopUp.enabled && wondCustomer.cardType !== CardType.IDENTIFICATION && wondCustomer.cardType !== CardType.CREDIT" @click="goToAutoTopUp()" :aria-label="$t('customerCard.ariaLabel.autoCharge')" aria-live="polite">{{ $t('customerCard.button.autoCharge') }}</Button>
      </template>
    </BlockContainer>
  </FlexLayout>
</template>

<script>
import Utils from "@/utils/utils";
import moment from "moment/moment";
import CryptoJS from "crypto-js";
import base32Encode from "base32-encode";
import {CardType, UserTypes, Workflow} from "@/constants/constants";
import VueLoading from "vue-loading-overlay";
import QrcodeVue from "qrcode.vue";
import RadialProgressBar from "vue-radial-progress";
import {accountService} from "@/services/api/accountService";
import {useAppStore} from "@/stores/app";
import {useTopUpRefreshStore} from "@/stores/topUpRefresh";
import BlockContainer from "@/components/layout/BlockContainer.vue";
import FlexLayout from "@/components/layout-components/Main/FlexLayout.vue";
import Button from "@/components/actions/Button.vue";

const TOP_UP_REFRESH_INTERVAL_MS = 2000;
const TOP_UP_REFRESH_MAX_ATTEMPTS = 10;

export default {
  name: 'customer-card',
  components:{
    Button,
    FlexLayout,
    BlockContainer,
    VueLoading,
    'qr-code' : QrcodeVue,
    'radial-progress-bar': RadialProgressBar,
  },
  setup() {
    const appStore = useAppStore();
    const topUpRefreshStore = useTopUpRefreshStore();
    return { appStore, topUpRefreshStore };
  },
  data() {
    return {
      isLoading: false,
      pendingCustomerCardPayments:[],
      cardNum: "",
      qrCode:"",
      qrCodeSeconds: 30,
      intervalId: 0,
      prepayBalanceCash: 0,
      customerCardWithCreditFunktion: false,
      creditBalance: 0,
      currencyCode: undefined,
      isParent: false,
      isChild: false,
      wondCustomer: {},
      customer:{},
      topUpRefreshAttempts: 0,
      topUpRefreshTimeoutId: null,
      resolveTopUpRefreshWait: null,
      isComponentDestroyed: false,
    };
  },
  computed: {
    CardType() {
      return CardType
    },
    Utils() { return Utils; },
    config() { return window.config; },
    balanceLabelKey() {
      return this.config?.CUSTOMER_CARD?.useMealBalance === true
        ? 'customerCard.label.mealBalance'
        : 'customerCard.label.balance';
    },
    additionalCards() {
      return Array.isArray(this.wondCustomer.additionalCards)
        ? this.wondCustomer.additionalCards.filter(card => card.valid)
        : [];
    },
  },
  methods: {
    formatDate(dateString){
      if(!dateString) return "";
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },
    formatPrice(priceString){
      return Utils.formatCurrency(priceString, this.config.ACCOUNT_BALANCE_CURRENCY, true, this.config.ACCOUNT_BALANCE_SEPARATOR);
    },
    goToTopUp() { this.$navigateToTopUp(); },
    goToAutoTopUp() { this.$navigateToAutoTopUp(); },
    wordToByteArray(wordArray) {
      const byteArray = [];
      for (let i = 0; i < wordArray.length; ++i) {
        const word = wordArray[i];
        for (let j = 3; j >= 0; --j) {
          byteArray.push((word >> 8 * j) & 0xFF);
        }
      }
      return byteArray;
    },
    startQrCodeInterval() {
      this.generateQrCode();
      this.intervalId = setInterval(() => {
        this.qrCodeSeconds -= 1;
        if(this.qrCodeSeconds === 0) {
          this.generateQrCode();
        }
      }, 1000);
    },
    clearQrInterval() {
      if (this.intervalId) {
        clearInterval(this.intervalId);
        this.intervalId = 0;
      }
    },
    async generateQrCode() {
      const config = this.config;
      const date = new Date();
      const newDateObj = moment(date).add(30, 'seconds').utc().format('yyyyMMDDHHmmss');
      let barcode = this.cardNum + newDateObj;

      // checksum (Code39 modulo 43)
      const code39Chars = "0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ-. $/+%";
      let sum = 0;
      for (let i = 0; i < barcode.length; i++) {
        sum += code39Chars.indexOf(barcode.charAt(i));
      }
      const checksumChar = code39Chars[sum % 43];
      barcode += checksumChar;

      const C = CryptoJS;
      const bytes = C.enc.Utf8.parse(barcode);

      // Static key + IV (todo consider externalizing for security)
      const key = new Uint8Array([60, 175, 77, 70, 58, 137, 151, 252, 5, 30, 164, 241, 182, 3, 136, 151, 129, 196, 108, 49, 255, 108, 131, 159, 207, 90, 24, 11, 38, 215, 110, 138]);
      const IV = new Uint8Array([140, 184, 62, 82, 119, 188, 41, 89, 188, 112, 122, 223, 51, 65, 170, 21]);
      const aes = C.algo.AES.createEncryptor(C.lib.WordArray.create(key), {
        mode: C.mode.CBC,
        padding: C.pad.Pkcs7,
        iv: C.lib.WordArray.create(IV)
      });

      const encrypted = aes.finalize(bytes);
      const uint8Array = this.wordToByteArray(encrypted.words);
      const uint8ArrayFinal = new Uint8Array(uint8Array);
      let qrcode = base32Encode(uint8ArrayFinal, 'RFC4648', {padding: false});
      qrcode = "TCPOSAPP" + qrcode;

      if (config.USE_SHORT_QRCODE) {
        const response = await accountService.getShortQRCode(qrcode);
        this.qrCode = response.exception !== undefined || response.QRCodeProperties === undefined ? "ERROR" : response.QRCodeProperties.shortCode;
      } else {
        this.qrCode = qrcode;
      }

      this.qrCodeSeconds = 30;
      this.$forceUpdate();
    },
    async loadCustomerCardInfo(showLoading = true) {
      const config = this.config;

      if (showLoading) {
        this.isLoading = true;
      }

      let result;
      try {
        result = await accountService.afterLogin();
      } finally {
        if (showLoading) {
          this.isLoading = false;
        }
      }

      if (result.exception !== undefined || !result.wondCustomer) {
        this.makeToast('danger', this.$t('customerCard.messages.error.loadInfos'));
        return false;
      }

      this.isParent = UserTypes.PARENT === result.customer.custom3;
      this.isChild = UserTypes.CHILD === result.customer.custom3;

      this.customer = result.customer;
      this.currencyCode = result.basketTile.currencyCode;
      this.wondCustomer = result.wondCustomer;
      this.cardNum = result.wondCustomer.cardNum;

      if (result.wondCustomer.cardType.toUpperCase() === "CREDIT") {
        let separator = Utils.getDecimalSeparator(result.wondCustomer.formattedCreditBalance);
        if(config.ACCOUNT_BALANCE_SEPARATOR) { separator = config.ACCOUNT_BALANCE_SEPARATOR; }
        this.creditBalance = result.wondCustomer.creditBalance.toFixed(config.CUSTOMER_BALANCE_DECIMAL_PLACES).replace('.', separator);
        this.customerCardWithCreditFunktion = true;
      } else {
        let separator = Utils.getDecimalSeparator(result.wondCustomer.formattedPrepayBalanceCash);
        if(config.ACCOUNT_BALANCE_SEPARATOR) { separator = config.ACCOUNT_BALANCE_SEPARATOR; }
        this.prepayBalanceCash = (result.wondCustomer.prepayBalanceCash + result.wondCustomer.prepayBalanceVoucher + result.wondCustomer.prepayBalanceBonus).toFixed(config.CUSTOMER_BALANCE_DECIMAL_PLACES).replace('.', separator);
      }

      if (!this.intervalId) {
        this.startQrCodeInterval();
      }

      this.pendingCustomerCardPayments = result.wondCustomer.pendingTopup && result.wondCustomer.pendingTopup.length
        ? result.wondCustomer.pendingTopup
        : [];

      return true;
    },
    waitForTopUpRefreshInterval() {
      return new Promise(resolve => {
        this.resolveTopUpRefreshWait = resolve;
        this.topUpRefreshTimeoutId = setTimeout(() => {
          this.topUpRefreshTimeoutId = null;
          this.resolveTopUpRefreshWait = null;
          resolve();
        }, TOP_UP_REFRESH_INTERVAL_MS);
      });
    },
    async refreshPendingTopUpBalance() {
      if (!this.topUpRefreshStore.hasConfirmedPendingRefresh) {
        return;
      }

      if (this.topUpRefreshStore.isSatisfiedBy(this.wondCustomer)) {
        this.topUpRefreshStore.clear();
        return;
      }

      while (
        this.topUpRefreshStore.hasConfirmedPendingRefresh &&
        this.topUpRefreshAttempts < TOP_UP_REFRESH_MAX_ATTEMPTS &&
        !this.isComponentDestroyed
      ) {
        this.topUpRefreshAttempts += 1;
        await this.waitForTopUpRefreshInterval();

        if (this.isComponentDestroyed) {
          return;
        }

        const loaded = await this.loadCustomerCardInfo(false);
        if (!loaded) {
          return;
        }

        if (this.topUpRefreshStore.isSatisfiedBy(this.wondCustomer)) {
          this.topUpRefreshStore.clear();
          return;
        }
      }

      if (this.topUpRefreshStore.hasConfirmedPendingRefresh) {
        console.warn("Top-up balance refresh did not complete before timeout.");
        this.topUpRefreshStore.clear();
      }
    },
  },
  async created() {
    this.appStore.setWorkflow(Workflow.TOP_UP);
    const loaded = await this.loadCustomerCardInfo();
    if (loaded) {
      await this.refreshPendingTopUpBalance();
    }
  },
  beforeDestroy() { // Vue 2 cleanup
    this.isComponentDestroyed = true;
    if (this.topUpRefreshTimeoutId) {
      clearTimeout(this.topUpRefreshTimeoutId);
      this.topUpRefreshTimeoutId = null;
    }
    if (this.resolveTopUpRefreshWait) {
      this.resolveTopUpRefreshWait();
      this.resolveTopUpRefreshWait = null;
    }
    this.clearQrInterval();
  },
  beforeUnmount() { // Vue 3 compatibility
    this.isComponentDestroyed = true;
    if (this.topUpRefreshTimeoutId) {
      clearTimeout(this.topUpRefreshTimeoutId);
      this.topUpRefreshTimeoutId = null;
    }
    if (this.resolveTopUpRefreshWait) {
      this.resolveTopUpRefreshWait();
      this.resolveTopUpRefreshWait = null;
    }
    this.clearQrInterval();
  }
};
</script>

<style>

/* QR Code Section Styling */
.qr-section {
  display: flex;
  justify-content: center;
  align-items: center;
}

.additional-card-numbers {
  display: inline-flex;
  flex-direction: column;
  vertical-align: top;
}

</style>
